import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import rmmbrLogo from '../assets/rmmbr.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ClientSignup() {
  const [searchParams] = useSearchParams();
  const invitationCode = searchParams.get('code');

  const [clientInfo, setClientInfo] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const { theme } = useTheme();
  const [cssVars, setCssVars] = useState(() => {
    if (typeof window !== 'undefined') {
      const root = getComputedStyle(document.documentElement);
      return {
        bgPrimary: root.getPropertyValue('--bg-primary').trim(),
        bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
        textPrimary: root.getPropertyValue('--text-primary').trim(),
        textSecondary: root.getPropertyValue('--text-secondary').trim(),
        borderColor: root.getPropertyValue('--border-color').trim(),
        accent: root.getPropertyValue('--accent').trim(),
        accentHover: root.getPropertyValue('--accent-hover').trim()
      };
    }
    return { 
      bgPrimary: '#1a1816', 
      bgSecondary: '#2a2520', 
      textPrimary: '#ffffff',
      textSecondary: '#d1d5db',
      borderColor: '#3a352f',
      accent: '#D4A574',
      accentHover: '#C59D52'
    };
  });

  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Update CSS vars when theme changes
    if (typeof window !== 'undefined') {
      const root = getComputedStyle(document.documentElement);
      setCssVars({
        bgPrimary: root.getPropertyValue('--bg-primary').trim(),
        bgSecondary: root.getPropertyValue('--bg-secondary').trim(),
        textPrimary: root.getPropertyValue('--text-primary').trim(),
        textSecondary: root.getPropertyValue('--text-secondary').trim(),
        borderColor: root.getPropertyValue('--border-color').trim(),
        accent: root.getPropertyValue('--accent').trim(),
        accentHover: root.getPropertyValue('--accent-hover').trim()
      });
    }
  }, [theme]);

  useEffect(() => {
    if (invitationCode) {
      validateInvitation();
    } else {
      setError('No invitation code provided');
      setValidating(false);
    }
  }, [invitationCode]);

  const validateInvitation = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/auth/validate-invitation/${invitationCode}`
      );

      if (response.data.valid) {
        setClientInfo(response.data);
        setEmail(response.data.clientEmail || '');
        setName(response.data.clientName || '');
      }
    } catch (error) {
      console.error('Invalid invitation:', error);
      setError(error.response?.data?.error || 'Invalid or expired invitation code');
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    const result = await register(email, password, name, invitationCode);

    if (result.success) {
      navigate('/client', { replace: true });
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  if (validating) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
          color: cssVars.textPrimary || 'var(--text-primary)'
        }}
      >
        <div className="text-center">
          <div 
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: cssVars.accent || 'var(--accent)' }}
          ></div>
          <p style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}>
            Validating invitation...
          </p>
        </div>
      </div>
    );
  }

  if (error && !clientInfo) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
          color: cssVars.textPrimary || 'var(--text-primary)'
        }}
      >
        <div className="max-w-md w-full">
          <div 
            className="rounded-2xl shadow-xl p-8 text-center"
            style={{ 
              backgroundColor: cssVars.bgSecondary || 'var(--bg-secondary)',
              border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`
            }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}
            >
              <svg className="w-8 h-8" style={{ color: '#ef4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 
              className="text-2xl font-bold mb-2"
              style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
            >
              Invalid Invitation
            </h2>
            <p 
              className="mb-6"
              style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
            >
              {error}
            </p>
            <Link
              to="/login"
              className="inline-block py-2 px-6 rounded-lg font-medium transition"
              style={{ 
                backgroundColor: cssVars.accent || 'var(--accent)',
                color: '#ffffff'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = cssVars.accentHover || 'var(--accent-hover)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = cssVars.accent || 'var(--accent)'}
            >
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
        color: cssVars.textPrimary || 'var(--text-primary)'
      }}
    >
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={rmmbrLogo} alt="RMMBR" className="h-8 w-auto" />
          </div>
          <h1 
            className="text-4xl font-bold mb-2"
            style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
          >
            Welcome to RMMBR Method
          </h1>
          <p style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}>
            Create your account to get started
          </p>
        </div>

        {/* Signup Card */}
        <div 
          className="rounded-2xl shadow-xl p-8"
          style={{ 
            backgroundColor: cssVars.bgSecondary || 'var(--bg-secondary)',
            border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`
          }}
        >
          <div className="mb-6">
            <div 
              className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-3"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}
            >
              <svg className="w-6 h-6" style={{ color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p 
              className="text-center text-sm"
              style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
            >
              Invitation verified for <span 
                className="font-semibold"
                style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
              >
                {clientInfo?.clientName}
              </span>
            </p>
          </div>

          {error && (
            <div 
              className="mb-4 p-4 rounded-lg text-sm"
              style={{ 
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444'
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label 
                htmlFor="name" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
              >
                Full Name
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg transition"
                style={{ 
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = cssVars.accent || 'var(--accent)';
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = `0 0 0 2px ${cssVars.accent}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Your name"
              />
            </div>

            <div>
              <label 
                htmlFor="email" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg transition"
                style={{ 
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = cssVars.accent || 'var(--accent)';
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = `0 0 0 2px ${cssVars.accent}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label 
                htmlFor="password" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-4 py-3 rounded-lg transition"
                style={{ 
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = cssVars.accent || 'var(--accent)';
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = `0 0 0 2px ${cssVars.accent}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label 
                htmlFor="confirmPassword" 
                className="block text-sm font-medium mb-2"
                style={{ color: cssVars.textPrimary || 'var(--text-primary)' }}
              >
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg transition"
                style={{ 
                  backgroundColor: cssVars.bgPrimary || 'var(--bg-primary)',
                  border: `1px solid ${cssVars.borderColor || 'var(--border-color)'}`,
                  color: cssVars.textPrimary || 'var(--text-primary)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = cssVars.accent || 'var(--accent)';
                  e.target.style.outline = 'none';
                  e.target.style.boxShadow = `0 0 0 2px ${cssVars.accent}40`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = cssVars.borderColor || 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Re-enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: cssVars.accent || 'var(--accent)',
                color: '#ffffff'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = cssVars.accentHover || 'var(--accent-hover)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = cssVars.accent || 'var(--accent)';
                }
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p 
              className="text-sm"
              style={{ color: cssVars.textSecondary || 'var(--text-secondary)' }}
            >
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium transition"
                style={{ 
                  color: cssVars.accent || 'var(--accent)'
                }}
                onMouseEnter={(e) => e.target.style.color = cssVars.accentHover || 'var(--accent-hover)'}
                onMouseLeave={(e) => e.target.style.color = cssVars.accent || 'var(--accent)'}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientSignup;
